import { useContact } from '@/context/Contact';
import { PowerAppsTriggerEventNames, usePowerAppsTrigger } from '@/context/PowerApps';
import { PrivacyConsentModal } from '@/features/shared/portal/PrivacyConsentModal';
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { usePortalPrivacyConsentModal } from './hooks/usePortalPrivacyConsentModal';
import { usePortalPrivacyConsentDatalayer } from './PortalPrivacyConsent.datalayer';
import { PrivacyConsentValues } from './PortalPrivacyConsent.interface';

export type PrivacyConsentUpdateCallback = (given: boolean) => void;

export type PortalPrivacyConsentContextValue = {
  privacyConsent: PrivacyConsentValues | null;
  onPrivacyConsentUpdated: (callback: PrivacyConsentUpdateCallback) => void;
};

export const PortalPrivacyConsentContext = createContext<PortalPrivacyConsentContextValue>({
  privacyConsent: null,
  onPrivacyConsentUpdated: () => {},
});

export enum PropertyUpdateNames {
  PORTAL_PRIVACY_CONSENT = 'gen_portalprivacyconsent',
}

export const PortalPrivacyConsentProvider = ({ children }: { children: React.ReactNode }) => {
  const { contactInfo } = useContact();
  const { modalProps, availableForDisplay } = usePortalPrivacyConsentModal();
  const { emitImpressionPopup, emitToggleConsent } = usePortalPrivacyConsentDatalayer();
  const waitingForPortalPrivacyConsent = React.useRef<PrivacyConsentUpdateCallback[]>([]);
  const [isUpdatedPrivacyConsentGiven, setIsUpdatedPrivacyConsentGiven] = React.useState<any>(null);
  const isPrivacyConsentGiven = React.useMemo(() => {
    return contactInfo?.portalPrivacyConsent === PrivacyConsentValues.CONSENT_ACCEPTED;
  }, [contactInfo?.portalPrivacyConsent]);

  // it wont be displayed if the rb doest not have content
  const hasModalContent = useMemo(() => modalProps?.title && modalProps?.content, [modalProps]);
  const displayConsentModal = useMemo(
    () => availableForDisplay && Boolean(hasModalContent),
    [availableForDisplay, hasModalContent],
  );

  useEffect(() => {
    if (!displayConsentModal) return;
    emitImpressionPopup(modalProps?.title);
  }, [modalProps?.title, emitImpressionPopup, displayConsentModal]);

  const flushWaitingForPortalPrivacyConsentPool = React.useCallback((given: boolean) => {
    waitingForPortalPrivacyConsent.current.map((cb: PrivacyConsentUpdateCallback) => cb(given));
    waitingForPortalPrivacyConsent.current = [];
  }, []);

  /**
   * Handle PowerApps Trigger
   */
  const handlePowerAppsTrigger = React.useCallback(
    ({ eventName, data }: any) => {
      switch (eventName) {
        case PowerAppsTriggerEventNames.PROPERTY_UPDATE:
          if (data?.name !== PropertyUpdateNames.PORTAL_PRIVACY_CONSENT) break;

          console.log('update privacy consent', data?.value);
          // Update State
          setIsUpdatedPrivacyConsentGiven(data?.value === 'true');
          break;
        case PowerAppsTriggerEventNames.FORM_SUBMITTED:
          if (isUpdatedPrivacyConsentGiven === null) break;
          if (isUpdatedPrivacyConsentGiven === isPrivacyConsentGiven) break;

          // Dispatch Event
          console.log('dispatch event and update contact info');
          setIsUpdatedPrivacyConsentGiven(null);
          emitToggleConsent({ given: isUpdatedPrivacyConsentGiven });
          flushWaitingForPortalPrivacyConsentPool(isUpdatedPrivacyConsentGiven);
          break;
        default:
          break;
      }
    },
    [
      isPrivacyConsentGiven,
      isUpdatedPrivacyConsentGiven,
      emitToggleConsent,
      flushWaitingForPortalPrivacyConsentPool,
    ],
  );

  usePowerAppsTrigger(handlePowerAppsTrigger);

  const onPrivacyConsentUpdated = React.useCallback((cb: PrivacyConsentUpdateCallback) => {
    waitingForPortalPrivacyConsent.current.push(cb);
  }, []);

  return (
    <PortalPrivacyConsentContext.Provider
      value={{
        privacyConsent: (contactInfo?.portalPrivacyConsent as PrivacyConsentValues) || null,
        onPrivacyConsentUpdated,
      }}
    >
      <PrivacyConsentModal open={displayConsentModal} contentProps={modalProps} />
      {children}
    </PortalPrivacyConsentContext.Provider>
  );
};

export const PreviewPortalPrivacyConsentProvider = ({ children }: PropsWithChildren<any>) => {
  return (
    <PortalPrivacyConsentContext.Provider
      value={{ privacyConsent: null, onPrivacyConsentUpdated: () => {} }}
    >
      {children}
    </PortalPrivacyConsentContext.Provider>
  );
};

export const usePortalPrivacyConsent = () => {
  const context = useContext(PortalPrivacyConsentContext);
  if (!context)
    throw new Error('usePortalPrivacyConsent must be used within a PortalPrivacyConsentProvider');

  return context;
};
